import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { BroadcastService } from '@services/broadcast-service';
import { ConnectionService } from '@services/connection-service';
import { fromSelectors } from '@store/selectors';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { fromActions } from '@store/actions';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { EventLoggerService } from '@services/event-logger-service';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { AppWebBridgeService } from '@services/app-web-bridge-service';
import { NotificationPermissionSheetComponent } from
  '@shared/bottom-sheet-layouts/notification-permission-sheet/notification-permission-sheet.component';
import { PaidPastQueriesSheetComponent } from './paid-past-queries-sheet/paid-past-queries-sheet.component';
import { ApiClientConstant, Table } from '@cureskin/api-client';
import { ExploreService } from '@services/explore-service/explore.service';
import { Regimen } from '../app-constant-types';
import { AppConfig } from '../app.config';
import { UserService } from "@services/user";
import { ExperimentService } from '@services/experiment';
import { RegimenService } from '@services/regimen-service';
import { AiAssistantService } from '@services/ai-assistant-service';
import { take } from 'rxjs/operators';
import { PaidAiChatQA } from '@services/experiment/experiment.service.types';

const DEFAULT_PAID_HELP_FAQS: PaidAiChatQA[] = [
  { englishQuestion: 'How is my kit designed?', hindiQuestion: 'मेरी किट कैसे बनाई गई है?', questionLanguageString: 'wyLqPc0WEl' },
  { englishQuestion: 'When will I see results?', hindiQuestion: 'मुझे नतीजे कब दिखेंगे?', questionLanguageString: 'lxpPDO5auY' },
  { englishQuestion: 'How long is the treatment?', hindiQuestion: 'इलाज कितने समय का है?', questionLanguageString: 'AHOQ3cNzwp' },
  { englishQuestion: 'What does the doctor do?', hindiQuestion: 'डॉक्टर क्या करते हैं?', questionLanguageString: '9tvPLQcEs0' },
  { englishQuestion: 'What are monthly checkups?', hindiQuestion: 'मासिक चेकअप क्या होते हैं?', questionLanguageString: '6yXQMxi5D1' },
  { englishQuestion: 'Why is photo needed?', hindiQuestion: 'फोटो की ज़रूरत क्यों है?', questionLanguageString: '5peyY5oAZP' },
];

@Component({
  selector: 'replacement',
  templateUrl: './support.html',
  styleUrls: ['./support.css'],
  standalone: false,
})
export class SupportComponent implements OnInit, OnDestroy {
  ViewTypes: {
    FirstPage: 'FirstPage';
    NextPage: 'NextPage';
    SingleView: 'SingleView';
  } = { FirstPage: 'FirstPage', NextPage: 'NextPage', SingleView: 'SingleView' };
  subscriptions: Array<Subscription> = [];
  user: any;
  experiments: any;
  question: any;
  isHelpfull: boolean;
  showChatSupportPrompt: boolean;
  showCallPopup: boolean;
  ticketInProcess: boolean;
  exploreSectionExperiment: boolean = false;
  questions: Array<string>;
  isNewHelpUiExp: boolean = false;
  viewType: string = this.ViewTypes.FirstPage;
  inProcess: boolean = true;
  queriesCount: number;
  getSupportQuestionFetch: Observable<Array<any>> = this.store.select(fromSelectors.selectSupportQuestions);
  recentSupportTicketList$: Observable<Record<string, any>[]> = this.store.select(fromSelectors.selectSupportOpenTicketList);
  allSupportTicketList$: Observable<Record<string, any>[]> = this.store.select(fromSelectors.selectAllSupportTicket);
  userStatus: any = { PAID: false };
  selectedIndex: number = -1;
  newHelpTreeExperiment: boolean = false;
  newSupportQuestionId: string;
  festivalDelayAlertEnabled: boolean;
  returnedFrom: string;
  isNotificationPermissionEnabled: any;
  pendingResponseMap: any;
  showExploreTab: boolean = false;
  private readonly exploreSubscription: Subscription;
  shouldShowPendingCallBanner: boolean = false;
  shouldShowUnpaidLlmChatBanner: boolean = false;
  showInstructionBanner: boolean = false;
  regimens: Regimen[];
  latestActiveRegimen: Regimen;
  isKeyboardVisible: boolean = false;
  showAcquisitionFaqs: boolean = false;
  paidQueryDraft: string = '';
  paidHelpFaqs: PaidAiChatQA[] = [];
  private paidHelpFaqText = new Map<string, string>();

  get isPaidHelpLandingPage(): boolean {
    return !!this.user?.isPaid()
      && this.viewType === this.ViewTypes.FirstPage
      && this.user?.get('orderState') === this.appConfig.Shared.User.OrderState.DELIVERED
      && this.experimentService.isNewHelpUiV2Enabled();
  }

  getSupportQueryCategory(query: Record<string, any>): string {
    const category = query?.llmResponse?.category;
    return category && category !== 'default' ? category : query?.category || 'Query';
  }

  get paidUserFirstName(): string {
    return this.user?.get('PatientName')?.trim().split(' ')[0] || '';
  }

  getPaidFaqText(faq: PaidAiChatQA): string {
    const language = (this.user?.get('languagePreference') || this.appConfig.Shared.Languages.EN).toLowerCase();
    const languageStringText = faq.questionLanguageString && this.paidHelpFaqText.get(faq.questionLanguageString);
    if (languageStringText) return languageStringText;
    return language === this.appConfig.Shared.Languages.HI && faq.hindiQuestion
      ? faq.hindiQuestion
      : faq.englishQuestion;
  }

  constructor(
    private eventLogger: EventLoggerService,
    private activatedRoute: ActivatedRoute,
    private store: Store,
    private router: Router,
    public appConfig: AppConfig,
    private broadcastService: BroadcastService,
    private connectionService: ConnectionService,
    private bottomSheet: MatBottomSheet,
    private appBridge: AppWebBridgeService,
    private readonly exploreService: ExploreService,
    private readonly userService: UserService,
    private readonly experimentService: ExperimentService,
    private readonly regimenService: RegimenService,
    private readonly aiAssistantService: AiAssistantService,
    private readonly changeDetectorRef: ChangeDetectorRef,
  ) {
    this.subscribe();
    this.exploreSubscription = this.exploreService.showExploreTab$.subscribe(
      (showTab: boolean) => {
        this.showExploreTab = showTab;
      }
    );
    this.broadcastService.on<boolean>('KEYBOARD_VISIBLE').subscribe(isVisible => {
      this.isKeyboardVisible = isVisible;
    });
  }

  back(): void {
    this.router.navigate(['/home'], {});
  }

  async ngOnInit(): Promise<void> {
    this.appBridge.setStatusBarColor('gray100');
    this.user = this.connectionService.getActingUser();
    await this.experimentService.experimentsLoaded;
    await this.loadPaidHelpFaqs();
    this.showAcquisitionFaqs = this.userService.isNewOrUnpaidUser(this.user) && this.experimentService.isFaqInAcquisitionHelpEnabled();
    this.regimens = await this.connectionService.fetchRegimens();
    this.setRegimenPreparationBannerState();
    if (this.regimens.length > 1) {
      this.latestActiveRegimen =
        this.regimens.find((regimen) => regimen.class === ApiClientConstant.Regimen.Class.FACE) ||
        this.regimens.find((regimen) => regimen.class === ApiClientConstant.Regimen.Class.HAIR) ||
        null;
    } else {
      this.latestActiveRegimen = this.regimens[0];
    }
    this.returnedFrom = this.activatedRoute.snapshot.queryParams?.returnedFrom;
    this.findUserStatus();
    if (!this.question.objectId) {
      this.updateQueryCount();
    }
    this.experiments = await this.connectionService.findUserActiveExperiments();
    await this.findActiveExperiments(this.experiments);
    this.eventLogger.cleverTapEvent('pageOpen', JSON.stringify({ pageName: 'support' }));

    this.exploreService.handleExploreTabExperiment(
      this.experiments,
      this.user?.get('orderState')
    );
  }

  private async loadPaidHelpFaqs(): Promise<void> {
    this.paidHelpFaqText.clear();
    if (!this.experimentService.isNewHelpUiV2Enabled()) {
      this.paidHelpFaqs = [];
      return;
    }

    const variant = this.experimentService.getNewHelpUiV2Experiment()?.variant;
    const language = (this.user?.get('languagePreference') || this.appConfig.Shared.Languages.EN).toLowerCase();
    const configuredLanguages = variant?.languages;
    const configuredPages = variant?.pagesToShow;
    if (configuredPages?.length && !configuredPages.includes('support')) {
      this.paidHelpFaqs = [];
      return;
    }
    const configuredQAs = variant?.defaultQAs;
    const qas = configuredQAs?.length ? configuredQAs : DEFAULT_PAID_HELP_FAQS;
    this.paidHelpFaqs = qas.filter((qa: PaidAiChatQA): boolean => {
      if (!configuredLanguages?.length) return true;
      return configuredLanguages.includes(language);
    });
    if (!this.paidHelpFaqs.length) this.paidHelpFaqs = DEFAULT_PAID_HELP_FAQS;

    const languageStringIds = this.paidHelpFaqs
      .map((qa: PaidAiChatQA): string | undefined => qa.questionLanguageString)
      .filter((id: string | undefined): id is string => !!id);
    if (!languageStringIds.length) return;

    try {
      const languageStrings = await this.connectionService.getLanguageStringsById(languageStringIds);
      const selectedLanguage = (this.user?.get('languagePreference') || this.appConfig.Shared.Languages.EN).toLowerCase();
      languageStrings?.forEach((languageString: { objectId?: string; id?: string; get?: (language: string) => string }): void => {
        const text = languageString?.get?.(selectedLanguage);
        const id = languageString?.objectId || languageString?.id;
        if (id && text) this.paidHelpFaqText.set(id, text);
      });
    } catch {
    }
  }

  setRegimenPreparationBannerState(): Regimen | void {
    this.regimens = this.regimens.map((regimen: Regimen) => {
      if (!regimen.activeFrom) return { ...regimen };
      const activeTime: any = new Date(regimen.activeFrom?.iso);
      const curTime: any = new Date();
      if (activeTime - curTime > 0) {
        setTimeout(():void => {
          this.setRegimenPreparationBannerState();
        }, activeTime - curTime);
      }
      return {
        ...regimen,
        isPreparationTimePending: activeTime - curTime > 0,
      };
    });
  }

  findActiveExperiments(experiments: any): any {
    experiments.forEach(async (exp: any): Promise<void> => {
      if (exp.key === 'new_help_ui') {
        this.isNewHelpUiExp = !!exp;
        await this.getPendingResponseMap();
      }
      if (exp.key === 'festival_delay_banner_enabled') this.festivalDelayAlertEnabled = !!exp;
      if (exp.key === 'new_help_tree') {
        this.newSupportQuestionId = exp.variant.questionId;
        this.newHelpTreeExperiment = true;
      }
      if (exp.key === 'explore_tab') this.exploreSectionExperiment = exp;
      if (exp.key === 'notification_permission_popup') setTimeout(async (): Promise<void> => await this.openBottomSheet(), 1000);
    });
    this.showInstructionBanner = this.userService.isUnpaidUser(this.user)
      && !!this.latestActiveRegimen
      && !this.regimenService.isAnyRegimenPreparationTimePending(this.regimens);
  }

  shouldShowDoctorCallBanner(value: boolean): void {
    this.shouldShowPendingCallBanner = value;
  }

  shouldShowLlmChatBanner(value: boolean): void {
    this.shouldShowUnpaidLlmChatBanner = value;
  }

  async getPendingResponseMap(): Promise<void> {
    this.pendingResponseMap = await this.connectionService.findAllPendingTicketWithAwaitingResponse();
  }

  async openBottomSheet(): Promise<void> {
    await this.appBridge.requestNotificationStatus()?.then((status) => {
      this.isNotificationPermissionEnabled = status;
      if (!this.isNotificationPermissionEnabled && this.returnedFrom === 'supportTicket') {
        this.bottomSheet.open(NotificationPermissionSheetComponent, {
          data: {
            content: 'support-ticket',
            callback: (): void => {
              this.openNativePermissionPopup();
            },
          },
        });
      }
    });
  }

  openNativePermissionPopup(): void {
    this.appBridge.requestNotificationPermission();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((each: Subscription): void => each.unsubscribe());
    this.subscriptions = [];
    if (this.exploreSubscription) {
      this.exploreSubscription.unsubscribe();
    }
  }

  async viewTicket(query: { [key: string]: any }): Promise<void> {
    await this.router.navigate([`/support/ticket/${query.objectId}`], { queryParams: { username: this.user?.get('username') } });
  }

  async startPaidQuery(suggestedQuery?: string): Promise<void> {
    if (this.ticketInProcess) return;
    const queryTitle = (suggestedQuery || this.paidQueryDraft).trim();
    if (!queryTitle) return;

    if (this.experimentService.isNewHelpUiV2Enabled()) {
      this.openPaidAiChat(queryTitle);
      return;
    }

    this.ticketInProcess = true;
    try {
      const questionId = this.newHelpTreeExperiment ? this.newSupportQuestionId : '8L9eKnSUPe';
      const { supportTicketId }: { supportTicketId: string } = await this.connectionService.createTicket(questionId, queryTitle);
      await this.router.navigate([`/support/ticket/${supportTicketId}`], { replaceUrl: true });
    } finally {
      this.ticketInProcess = false;
    }
  }

  logPaidHelpClick(eventName: string, params: Record<string, unknown> = {}): void {
    this.eventLogger.trackEvent(eventName, params);
  }

  onPaidFaqClick(faq: PaidAiChatQA): void {
    const question = this.getPaidFaqText(faq);
    this.logPaidHelpClick('PAID_HELP_SUGGESTED_QUERY_CLICKED', {
      question,
      questionLanguageString: faq.questionLanguageString,
    });
    void this.startPaidQuery(question);
  }

  private openPaidAiChat(initialMessage: string): void {
    this.paidQueryDraft = '';
    this.aiAssistantService.requestOpen({ initialMessage });
  }

  openPaidPastQueries(source: 'past_queries' | 'recent_queries_chevron' = 'past_queries'): void {
    this.logPaidHelpClick('PAID_HELP_PAST_QUERIES_CLICKED', { source });
    this.allSupportTicketList$.pipe(take(1)).subscribe((queries: Record<string, any>[]): void => {
      this.bottomSheet.open(PaidPastQueriesSheetComponent, {
        data: { queries },
        panelClass: ['user-query-chat-panel', 'paid-past-queries-panel'],
        backdropClass: 'paid-past-queries-backdrop',
        closeOnNavigation: false,
        disableClose: false,
      });
    });
  }

  async viewPaidRecentQuery(query: Record<string, any>): Promise<void> {
    this.logPaidHelpClick('PAID_HELP_RECENT_QUERY_CLICKED', { supportTicketId: query?.objectId });
    await this.viewTicket(query);
  }

  private subscribe(): void {
    this.subscriptions.push(this.activatedRoute.params.subscribe((params: Params): void => {
      this.isHelpfull = undefined;
      if (params.id) {
        this.question = { objectId: params.id };
        this.viewType = this.ViewTypes.NextPage;
        this.store.dispatch(fromActions.GetSupportQuestionFromStore({
          query: { where: { objectId: params.id, active: true } },
          context: { inputs: { ...this.activatedRoute.snapshot.queryParams } },
        }));
      } else {
        this.question = { outputComponent: this.appConfig.Shared.Support.component.SELECT_SUB_QUERY };
        this.viewType = this.ViewTypes.FirstPage;
        this.store.dispatch(fromActions.GetSupportQuestionFromStore({
          query: { where: { start: true, active: true }, descending: 'rank' },
        }));
        this.store.dispatch(fromActions.SupportRecentTicketFetchBegin({}));
      }
    }));
    this.subscriptions.push(this.store.select(fromSelectors.selectSupportQuestions).subscribe((questions: Array<any>): void => {
      if (!questions) {
        return;
      }
      if (questions.length === 1 && questions[0].objectId === this.question.objectId) {
        this.question = questions[0];
        const supportQuestion = new Table.SupportQuestion();
        supportQuestion.id = this.question.objectId;
        this.store.dispatch(fromActions.GetSupportQuestionFromStore({
          query: { where: { parentSupportQuestions: supportQuestion, active: true } },
        }));
        return;
      }
      this.inProcess = false;
      this.user = this.connectionService.getActingUser();
      const userLanguage = this.user?.get('languagePreference') || this.appConfig.Shared.Languages.EN;
      if (this.user?.isPaid()) {
        this.questions = questions
          .filter((question: any): any => question?.objectId === '8L9eKnSUPe')
          .map((question: any): any => ({ ...question, question: this.getNewQueryString(userLanguage), listViewType: 'Next', testId: 'help-new-query' }));
      } else {
        this.questions = questions.filter((question: any): boolean => question?.objectId !== 'i7rwngsUto');
      }
      this.changeDetectorRef.detectChanges();
    }));
  }

  getNewQueryString(userLanguage: any): string {
    switch (userLanguage) {
      case (this.appConfig.Shared.Languages.HI): return 'नया सवाल';
      case (this.appConfig.Shared.Languages.KN): return 'ಹೊಸ ಪ್ರಶ್ನೆ';
      case (this.appConfig.Shared.Languages.TE): return 'కొత్త ప్రశ్న';
      case (this.appConfig.Shared.Languages.TA): return 'புதிய கேள்வி';
      case (this.appConfig.Shared.Languages.MR): return 'नवीन प्रश्न';
      case (this.appConfig.Shared.Languages.BN): return 'নতুন প্রশ্ন';
      case (this.appConfig.Shared.Languages.ML): return 'പുതിയ ചോദ്യം';
      case (this.appConfig.Shared.Languages.EN): return 'New query';
      default: return 'New query';
    }
  }

  /**
   * Logs the helpfull status of that question (like / dislke) and shows a popup for chat support when ppl click on dislike.
   *
   * Don't show popup and chat support if the support section is `place a new order`
   * ie `outputComponent !== 'PLACE_NEW_ORDER'`
   * Don't show popup and chat support if the support section is `issue with my order`
   * ie `objectId !== 'GTjJHupZWB'`
   *
   * @param {boolean} isHelpfull
   */
  async updateFeedback(isHelpfull: boolean): Promise<void> {
    this.isHelpfull = isHelpfull;
    this.eventLogger.trackEvent('SUPPORT_HELPFULL_FEEDBACK', { questionId: this.question.objectId, isHelpfull });

    if (
      isHelpfull === false
      && this.question.treeNode
      && this.question.outputComponent !== this.appConfig.Shared.Support.component.PLACE_NEW_ORDER
      && this.question.objectId !== 'GTjJHupZWB'
    ) {
      this.showChatSupportPrompt = true;
    }
  }

  onCallNeeded(): void {
    this.eventLogger.trackEvent('SUPPORT_CALL_REQUESTED', { questionId: this.question.objectId, callPressed: true });
  }

  /**
   * Creates support ticket for the question and routes to ticket view.
   */
  async createTicket(): Promise<void> {
    if (this.ticketInProcess) return;
    this.ticketInProcess = true;
    let questionId = '8L9eKnSUPe';
    if (this.question.objectId === '8L9eKnSUPe' && this.newHelpTreeExperiment) {
      questionId = this.newSupportQuestionId;
    }
    try {
      const { supportTicketId }: { supportTicketId: string } = await this.connectionService.createTicket(questionId);
      this.eventLogger.trackEvent('SUPPORT_TICKET_CREATED_ON_DISLIKE', { questionId });
      await this.router.navigate([`/support/ticket/${supportTicketId}`], { replaceUrl: true });
    } finally {
      this.ticketInProcess = false;
    }
  }

  async viewAllQuery(): Promise<void> {
    await this.connectionService
      .navigateToURL(`/support/tickets?username=${this.activatedRoute.snapshot.queryParams.username || this.user?.get('username')}`);
  }

  private async updateQueryCount(): Promise<void> {
    this.queriesCount = await this.connectionService.fetchSupportTicketCount(this.user);
  }

  cameraClick(): void {
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'help-camera' }));
    this.connectionService.navigateToURL('/user/instantCheckup');
  }

  cameraClickHome(): void {
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'home-camera' }));
    this.connectionService.navigateToURL('/user/instantCheckup');
  }

  truncateString(str: string, maxLength: number): string {
    if (str?.length > maxLength) {
      return `${str.slice(0, maxLength)}...`;
    }
    return str || '';
  }

  shopClick(): void {
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'home-shop' }));
  }
  exploreClick(): void {
    this.eventLogger.cleverTapEvent('click-explore', JSON.stringify({ pageName: 'explore' }));
  }

  onTabChange(index: number): any {
    this.findUserStatus();
    if (index === this.selectedIndex) return 0;
    let queryParams: any;
    switch (index) {
      case 3: {
        queryParams = { tab: 'explore' };
        break;
      }
      case 4: {
        queryParams = { tab: 'shop' };
        break;
      }
      case 2: {
        queryParams = { tab: 'regimen' };
        break;
      }
      case 0:
      default:
        queryParams = { tab: 'home' };
    }
    return this.router.navigate(['/user'], { queryParams });
  }

  get isBottomNavExperimentActive(): boolean {
    return this.experimentService.isBottomNavConfigActive();
  }

  async findUserStatus(): Promise<any> {
    if (this.user?.isPaid() && this.user.get('orderState') === 'DELIVERED') this.userStatus.PAID = true;
  }
}
